import { useState, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/lib/supabase";
import {
  useGetClubQuery,
  useFetchClubMembersQuery,
  useFetchPublicClubPaymentSettingsQuery,
  useFetchUserProfilesByIdsQuery,
} from "@/api/clubsApi";
import type { ClubMember } from "@/api/clubsApi";
import { userDisplayName } from "@tinyclub/shared/utils";
import { ClubPricingSelector } from "@/components/clubs/payments";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";

interface PricingSearch {
  source?: string;
  interval?: string;
  pay?: string;
  payment?: string;
  inviteCode?: string;
  from?: string;
  private_prelim?: string;
}

export const Route = createFileRoute("/clubs/$clubId_/pricing")({
  // No requireAuth - this is a public page
  validateSearch: (search: Record<string, unknown>): PricingSearch => ({
    source: typeof search.source === "string" ? search.source : undefined,
    interval: typeof search.interval === "string" ? search.interval : undefined,
    pay: typeof search.pay === "string" ? search.pay : undefined,
    payment: typeof search.payment === "string" ? search.payment : undefined,
    inviteCode: typeof search.inviteCode === "string" ? search.inviteCode : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    private_prelim: typeof search.private_prelim === "string" ? search.private_prelim : undefined,
  }),
  component: PricingPage,
});

function PricingPage() {
  const { clubId } = Route.useParams();
  const { source, interval, pay, payment, inviteCode, from, private_prelim } = Route.useSearch();
  const navigate = useNavigate();

  const [selectedInterval, setSelectedInterval] = useState<"month" | "year">("year");
  const [loading, setLoading] = useState(false);
  const [authProcessing, setAuthProcessing] = useState(false);
  const [currentSession, setCurrentSession] = useState<any>(null);

  // Fetch club data (public)
  const { data: club } = useGetClubQuery(clubId);
  const { data: paymentSettings } = useFetchPublicClubPaymentSettingsQuery(clubId);
  const { data: clubMembers } = useFetchClubMembersQuery(
    { clubId },
    { skip: !clubId }
  );

  // Get owner members
  const ownerMembers = useMemo(
    () => clubMembers?.filter((cm: ClubMember) => cm.role === "owner") || [],
    [clubMembers]
  );

  // Fetch owner profiles
  const ownerIds = useMemo(
    () => ownerMembers.slice(0, 3).map((m) => m.user_id).filter(Boolean) as string[],
    [ownerMembers]
  );
  const { data: adminProfiles = [] } = useFetchUserProfilesByIdsQuery(ownerIds, {
    skip: ownerIds.length === 0,
  });

  // Calculate pricing info
  const trialDays = paymentSettings?.membership_trial_days || 0;
  const monthlyPrice = paymentSettings?.membership_price_monthly || 0;
  const yearlyPrice = paymentSettings?.membership_price_yearly || 0;

  // Auto-select if only one interval available
  const availableIntervals = useMemo(() => {
    const intervals: ("month" | "year")[] = [];
    if (monthlyPrice > 0) intervals.push("month");
    if (yearlyPrice > 0) intervals.push("year");
    return intervals;
  }, [monthlyPrice, yearlyPrice]);

  useEffect(() => {
    if (availableIntervals.length === 1) {
      setSelectedInterval(availableIntervals[0]);
    }
  }, [availableIntervals]);

  // Handle auth token exchange from URL fragment (iOS handoff)
  useEffect(() => {
    async function handleAuthToken() {
      const hash = window.location.hash;
      if (!hash || !hash.includes("auth=")) return;

      const tokenMatch = hash.match(
        /auth=([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i
      );
      if (!tokenMatch?.[1]) return;

      const authToken = tokenMatch[1];
      setAuthProcessing(true);

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

        const response = await fetch(`${supabaseUrl}/functions/v1/exchange-auth-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: authToken,
            expectedAction: private_prelim === "true" ? "view_subscription" : "join_with_payment",
          }),
        });

        if (!response.ok) return;

        const data = await response.json();

        if (data.access_token && data.refresh_token) {
          const sessionSetPromise = new Promise<any>((resolve, reject) => {
            let resolved = false;
            const {
              data: { subscription },
            } = supabase.auth.onAuthStateChange((event, session) => {
              if (event === "SIGNED_IN" && !resolved) {
                resolved = true;
                subscription.unsubscribe();
                resolve(session);
              }
            });

            supabase.auth
              .setSession({
                access_token: data.access_token,
                refresh_token: data.refresh_token,
              })
              .then(({ error }) => {
                if (error && !resolved) {
                  resolved = true;
                  subscription.unsubscribe();
                  reject(error);
                }
              })
              .catch((error) => {
                if (!resolved) {
                  resolved = true;
                  subscription.unsubscribe();
                  reject(error);
                }
              });

            setTimeout(() => {
              if (!resolved) {
                subscription.unsubscribe();
                supabase.auth.getSession().then(({ data: { session } }) => {
                  resolved = true;
                  if (session) resolve(session);
                  else reject(new Error("Session setup timed out"));
                });
              }
            }, 10000);
          });

          try {
            const authenticatedSession = await sessionSetPromise;
            if (authenticatedSession) setCurrentSession(authenticatedSession);

            // Clean auth token from URL
            const baseUrl = window.location.href.split("#")[0];
            window.history.replaceState(null, "", baseUrl);
          } catch (error) {
            console.error("Failed to set session:", error);
          }
        }
      } catch (error) {
        console.error("Auth handoff error:", error);
      } finally {
        setAuthProcessing(false);
      }
    }

    handleAuthToken();
  }, [private_prelim]);

  // Handle payment success
  useEffect(() => {
    if (payment === "success" && source === "ios") {
      // Redirect to native app
      window.location.href = `tinyclub://clubs/${clubId}?payment=success`;
    }
  }, [payment, source, clubId]);

  // Handle Continue button
  const handleContinue = useCallback(async () => {
    try {
      const session = currentSession;

      if (!session) {
        // Redirect to login
        const returnUrl = `/clubs/${clubId}/pricing?pay=membership&interval=${selectedInterval}${source ? `&source=${source}` : ""}${inviteCode ? `&inviteCode=${inviteCode}` : ""}`;
        navigate({ to: "/login", search: { redirect: returnUrl } });
        return;
      }

      // Handle private_prelim flow
      if (private_prelim === "true" && inviteCode) {
        if (source === "ios" || source === "android") {
          window.location.href = `tinyclub://join/${inviteCode}?preliminaryComplete=true`;
        }
        return;
      }

      setLoading(true);
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const origin = window.location.origin;

      const successParams = new URLSearchParams();
      successParams.set("payment", "success");
      if (source) successParams.set("source", source);
      if (inviteCode) successParams.set("inviteCode", inviteCode);

      const cancelParams = new URLSearchParams();
      cancelParams.set("payment", "cancelled");
      if (source) cancelParams.set("source", source);
      if (inviteCode) cancelParams.set("inviteCode", inviteCode);

      const successUrl = `${origin}/clubs/${clubId}/pricing?${successParams.toString()}`;
      const cancelUrl = `${origin}/clubs/${clubId}/pricing?${cancelParams.toString()}`;

      const response = await fetch(`${supabaseUrl}/functions/v1/create-club-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          clubId,
          userId: session.user.id,
          paymentType: "membership",
          interval: selectedInterval,
          successUrl,
          cancelUrl,
          platform: "web",
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || "Failed to create checkout session");
      }

      window.location.href = responseData.checkoutUrl;
    } catch (error) {
      console.error("Error in handleContinue:", error);
    } finally {
      setLoading(false);
    }
  }, [clubId, selectedInterval, source, inviteCode, navigate, private_prelim, currentSession]);

  // Auto-trigger payment if returning from login
  useEffect(() => {
    async function checkAndTriggerAction() {
      if (pay === "membership" && interval && (interval === "month" || interval === "year")) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          setSelectedInterval(interval as "month" | "year");
          setCurrentSession(session);
          handleContinue();
        }
      }
    }
    checkAndTriggerAction();
  }, [pay, interval]);

  // Render admin info
  const renderAdminInfo = () => {
    if (!adminProfiles || adminProfiles.length === 0) return null;

    const adminCount = adminProfiles.length;
    let hostedByText = "";
    if (adminCount === 1) {
      hostedByText = userDisplayName(adminProfiles[0]);
    } else if (adminCount === 2) {
      hostedByText = `${userDisplayName(adminProfiles[0])} and ${userDisplayName(adminProfiles[1])}`;
    } else {
      hostedByText = `${userDisplayName(adminProfiles[0])} and ${adminCount - 1} ${adminCount - 1 === 1 ? "other" : "others"}`;
    }

    return (
      <div className="mb-8">
        <div className="flex items-center">
          <div className="flex -space-x-3">
            {adminProfiles.slice(0, 3).map((admin, index) => (
              <div
                key={admin.id}
                className="w-8 h-8 rounded-full border-2 border-background overflow-hidden"
                style={{ zIndex: 3 - index }}
              >
                {admin.avatar_small ? (
                  <img
                    src={admin.avatar_small}
                    alt={userDisplayName(admin)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-surface-1" />
                )}
              </div>
            ))}
          </div>
          <p className="text-body-2-medium text-text-secondary ml-3">
            Hosted by {hostedByText}
          </p>
        </div>
      </div>
    );
  };

  // Loading state
  if (!club || !paymentSettings || authProcessing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-text-primary border-t-transparent rounded-full animate-spin mx-auto" />
          {authProcessing && (
            <p className="text-body-2 text-text-secondary mt-4">Setting up...</p>
          )}
        </div>
      </div>
    );
  }

  // Payment success state
  if (payment === "success") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-accent-positive/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-accent-positive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-h4 text-text-primary mb-2">Payment successful</h2>
          <p className="text-body-2 text-text-secondary mb-6">
            Your membership is now active. Welcome to the club!
          </p>
          <Button onClick={() => navigate({ to: "/clubs/$identifier", params: { identifier: clubId } })}>
            Go to club
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[632px] w-full mx-auto px-6 pt-6 pb-12">
        {/* Page Header */}
        <div className="text-center mb-6">
          <h1 className="text-h2 text-text-primary">{club.name}</h1>
        </div>

        {/* Admin Info */}
        {renderAdminInfo()}

        {/* Preliminary pricing description */}
        {private_prelim === "true" && (
          <div className="mb-4">
            <p className="text-body-1 text-text-secondary">
              Choose a plan — cancel anytime. You'll complete the subscription process once a host approves your club
              membership.{trialDays ? ` Your ${trialDays}-day trial will begin then.` : ""}
            </p>
          </div>
        )}

        {/* Pricing Selector */}
        {paymentSettings && (
          <ClubPricingSelector
            paymentSettings={paymentSettings}
            selectedInterval={selectedInterval}
            onSelectInterval={setSelectedInterval}
            onContinue={handleContinue}
            loading={loading}
            trialDays={trialDays}
          />
        )}
      </div>
    </div>
  );
}
